import { oc, eventIterator } from "@orpc/contract";
import { z } from "zod";

/**
 * Audio Stream Event Schema - events emitted while transcribing an audio chunk.
 * The frontend chat input streams cumulative audio blobs and renders the
 * partial transcription deltas as the user speaks.
 */
export const AudioStreamEventSchema = z.discriminatedUnion("type", [
  // Partial (or final) transcription as speech is detected
  z.object({
    type: z.literal("transcription-delta"),
    text: z.string(),
    isFinal: z.boolean(),
    timestamp: z.number(),
  }),
  // Error event
  z.object({
    type: z.literal("error"),
    message: z.string(),
    code: z.string().optional(),
  }),
  // Complete event - transcription finished
  z.object({
    type: z.literal("complete"),
    finalText: z.string(),
    duration: z.number(), // milliseconds
  }),
]);

/**
 * Audio Input Configuration Schema
 */
export const AudioInputConfigSchema = z.object({
  sampleRate: z.number().default(16000),
  channels: z.number().default(1),
  enableVAD: z.boolean().default(true),
  vadSensitivity: z.enum(["low", "medium", "high"]).default("medium"),
  language: z.string().default("en"),
});

/**
 * Streaming Audio Input Schema
 */
export const StreamingAudioInputSchema = z.object({
  audioData: z.string(), // Base64 encoded audio chunk
  config: AudioInputConfigSchema.optional(),
  sessionId: z.string().uuid().optional(),
});

/**
 * Streaming Audio Contracts
 */
export const streamingAudioContracts = {
  /**
   * Stream audio input for real-time transcription.
   * Client sends a (cumulative) audio chunk, receives transcription events.
   */
  streamAudioInput: oc
    .input(StreamingAudioInputSchema)
    .output(eventIterator(AudioStreamEventSchema))
    .route({
      method: "POST",
      path: "/ai/audio/stream-input",
    }),
};

// Export types
export type AudioStreamEvent = z.infer<typeof AudioStreamEventSchema>;
export type AudioInputConfig = z.infer<typeof AudioInputConfigSchema>;
export type StreamingAudioInput = z.infer<typeof StreamingAudioInputSchema>;
